import type { Ton } from '../commun.js'
import { objet, type AppareilBref } from '../video/kit-modele.js'
import type { Point, Serie } from '../fiches/modele.js'
import type { Onduleur } from './batiment-modele.js'

/**
 * BÂTIMENT › ONDULEURS — modèle pur (27/10/2026).
 *
 * Demande d'Hugo : les 3 onduleurs, leurs tensions d'entrée et de sortie,
 * l'historique de consommation, et l'autonomie de CHAQUE appareil liée à son
 * propre onduleur. Sonde relève et garde tout (contrat 0009469) ; Gestion ne
 * fait qu'afficher :
 *
 *   GET /v1/power                        les onduleurs (ups[], champs enrichis)
 *   GET /v1/power/ups/:uid/history       courbes 24 h / 7 j / 30 j + passages sur batterie
 *   GET /v1/power/runtime                autonomie par appareil + déroulé d'une coupure
 *
 * Règles : une mesure absente s'écrit « — » ; les phrases de Sonde (`*_fr`)
 * s'affichent telles quelles ; un champ du contrat encore absent (Sonde pas
 * à jour) ne casse rien : la page dit seulement moins.
 */

export const CHEMIN_ONDULEURS = '/technique/batiment/onduleurs'
export const PERIODES_HISTORIQUE = ['24h', '7d', '30d'] as const
export type PeriodeHistorique = typeof PERIODES_HISTORIQUE[number]
export const LIBELLE_PERIODE: Record<PeriodeHistorique, string> = { '24h': '24 h', '7d': '7 j', '30d': '30 j' }

/* ═══════════════ Formes de Sonde ═══════════════ */

export interface Prise { id: string; label: string; status: 'on' | 'off' | null }
export type Alim = 'A' | 'B'

/** Un onduleur de `GET /v1/power`, avec les champs ajoutés le 27/10 (absents d'une Sonde plus ancienne). */
export interface OnduleurDetaille extends Onduleur {
  nut_name?: string | null
  serial?: string | null
  model?: string | null
  status?: 'OL' | 'OB' | 'LB' | null
  status_flags?: string[]
  status_fr?: string
  input_freq_hz?: number | null
  output_freq_hz?: number | null
  transfer_high_v?: number | null
  transfer_low_v?: number | null
  nominal_w?: number | null
  outlets?: Prise[]
  beeper?: 'enabled' | 'disabled' | 'muted' | null
  devices?: { device: AppareilBref; feed: Alim }[]
}

export interface AlimentationPoste { feed: Alim; ups_uid: string | null; ups_label: string | null }
export type LimiteAutonomie = 'ups' | 'shutdown_delay' | 'mains_only' | 'unknown'

export interface AutonomiePoste {
  device: AppareilBref
  feeds: AlimentationPoste[]
  runtime_min: number | null
  limited_by: LimiteAutonomie
  ups_uid: string | null
  ups_label: string | null
  text_fr: string
}

export interface EtapeCoupure {
  at_min: number
  ups_uid: string | null
  ups_label: string | null
  devices: string[]
  unreachable: string[]
  consequence_fr: string
}

export interface DocAutonomie {
  generated_at: string
  shutdown_delay_min: number
  ups: { uid: string; label: string; runtime_min: number | null; on_battery: boolean | null; at: string | null; data_age_s: number | null }[]
  devices: AutonomiePoste[]
  timeline: EtapeCoupure[]
  summary_fr: string
}

export interface PassageBatterie { from: string; to: string | null; duration_s: number | null; min_charge_pct: number | null; text_fr: string }

export interface DocHistoriqueOnduleur {
  generated_at: string
  uid: string
  label: string
  range: PeriodeHistorique
  from: string
  to: string
  level: 'raw' | '5m' | '1h'
  bucket_s: number
  gap_threshold_s: number
  series: Serie[]
  empty: string[]
  battery_periods: PassageBatterie[]
}

const liste = (x: unknown): x is unknown[] => Array.isArray(x)

export function estDocAutonomie(x: unknown): x is DocAutonomie {
  return objet(x) && typeof x['shutdown_delay_min'] === 'number' && liste(x['ups']) && liste(x['devices']) && liste(x['timeline'])
    && (x['devices'] as unknown[]).every((d) => objet(d) && objet(d['device']) && typeof (d['device'] as Record<string, unknown>)['uid'] === 'string' && liste(d['feeds']))
    && (x['timeline'] as unknown[]).every((e) => objet(e) && typeof e['at_min'] === 'number' && liste(e['devices']) && liste(e['unreachable']))
}

export function estDocHistoriqueOnduleur(x: unknown): x is DocHistoriqueOnduleur {
  return objet(x) && typeof x['uid'] === 'string' && typeof x['from'] === 'string' && typeof x['to'] === 'string'
    && liste(x['series']) && liste(x['battery_periods'])
}

/* ═══════════════ Chemins ═══════════════ */

export const UID_ONDULEUR = /^[a-z0-9][a-z0-9-]{1,62}$/

export function cheminHistorique(uid: string, periode: PeriodeHistorique): string | null {
  if (!UID_ONDULEUR.test(uid)) return null
  return `power/ups/${encodeURIComponent(uid)}/history?range=${periode}`
}

/** Ancre d'un onduleur dans la page : `#onduleur-<uid>`. */
export const ancreOnduleur = (uid: string): string => `onduleur-${uid}`
export const cheminOnduleur = (uid: string): string => `${CHEMIN_ONDULEURS}#${ancreOnduleur(uid)}`

/* ═══════════════ Lecture ═══════════════ */

/** « Onduleur 1 caméras », « Onduleur 2 réseau », « Onduleur 3 serveur » : dans l'ordre des numéros. */
export function onduleursRanges<T extends { device: AppareilBref }>(ups: readonly T[]): T[] {
  return [...ups].sort((a, z) => a.device.display_name.localeCompare(z.device.display_name, 'fr', { numeric: true }))
}

const LIBELLE_STATUT: Record<'OL' | 'OB' | 'LB', string> = { OL: 'Sur secteur', OB: 'Sur batterie', LB: 'Batterie faible' }

/**
 * L'état d'un onduleur : la phrase de Sonde (`status_fr`) si elle existe, sinon
 * le code NUT, sinon l'ancien couple on_battery / low_battery. Une donnée
 * périmée est grise, jamais « sur secteur ».
 */
export function etatDetaille(u: OnduleurDetaille): { ton: Ton; libelle: string; code: string | null } {
  const code = u.status ?? null
  const phrase = u.status_fr?.trim() || null
  if (u.device.state.greyed) return { ton: 'gris', libelle: 'Donnée périmée', code }
  const batterie = code === 'OB' || code === 'LB' || u.on_battery === true
  const faible = code === 'LB' || u.low_battery === true
  if (batterie) return { ton: 'err', libelle: phrase ?? (faible ? 'Sur batterie, batterie faible' : 'Sur batterie'), code }
  if (code === 'OL' || u.on_battery === false) {
    const surveiller = u.level === 'warning' || u.level === 'critical'
    return { ton: surveiller ? 'warn' : 'ok', libelle: phrase ?? (surveiller ? 'Sur secteur, à surveiller' : LIBELLE_STATUT.OL), code }
  }
  return { ton: 'gris', libelle: phrase ?? 'Alimentation inconnue', code }
}

export const LIBELLE_PRISE: Record<'on' | 'off', string> = { on: 'alimentée', off: 'coupée' }
export const libellePrise = (p: Prise): string => `${p.label} : ${p.status ? LIBELLE_PRISE[p.status] : '—'}`

export const LIBELLE_BIP: Record<'enabled' | 'disabled' | 'muted', string> = { enabled: 'Activé', disabled: 'Désactivé', muted: 'Coupé pour l’instant' }

/** « 38 min », « 1 h 05 », « — ». */
export function fmtMinutes(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return '—'
  const m = Math.max(0, Math.round(min))
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r === 0 ? `${h} h` : `${h} h ${String(r).padStart(2, '0')}`
}

/** « 12 min », « 2 h 10 » à partir de secondes. */
export const fmtDureeS = (s: number | null | undefined): string =>
  s === null || s === undefined || !Number.isFinite(s) ? '—' : s < 60 ? `${Math.max(0, Math.round(s))} s` : fmtMinutes(s / 60)

export const LIBELLE_LIMITE: Record<LimiteAutonomie, string> = {
  ups: 'autonomie de son onduleur',
  shutdown_delay: 'arrêt propre programmé',
  mains_only: 'sur secteur seul : s’éteint dès la coupure',
  unknown: 'alimentation non renseignée',
}

export const TON_LIMITE: Record<LimiteAutonomie, Ton> = { ups: 'ok', shutdown_delay: 'warn', mains_only: 'err', unknown: 'gris' }

/** « A : Onduleur 2 réseau · B : secteur seul ». */
export function texteAlimentations(feeds: readonly AlimentationPoste[]): string {
  if (feeds.length === 0) return 'non renseignée'
  return [...feeds].sort((a, z) => a.feed.localeCompare(z.feed))
    .map((f) => `${f.feed} : ${f.ups_uid ? f.ups_label ?? f.ups_uid : 'secteur seul'}`).join(' · ')
}

/** Les appareils de chaque onduleur, triés par autonomie puis par nom ; les autres à part. */
export function postesParOnduleur(doc: DocAutonomie): { parOnduleur: Map<string, AutonomiePoste[]>; sans: AutonomiePoste[] } {
  const parOnduleur = new Map<string, AutonomiePoste[]>()
  const sans: AutonomiePoste[] = []
  const tri = (a: AutonomiePoste, z: AutonomiePoste) =>
    (a.runtime_min ?? Infinity) - (z.runtime_min ?? Infinity) || a.device.display_name.localeCompare(z.device.display_name, 'fr', { numeric: true })
  for (const p of doc.devices) {
    const cles = new Set(p.feeds.map((f) => f.ups_uid).filter((u): u is string => !!u))
    if (cles.size === 0) { sans.push(p); continue }
    for (const u of cles) parOnduleur.set(u, [...(parOnduleur.get(u) ?? []), p])
  }
  for (const l of parOnduleur.values()) l.sort(tri)
  sans.sort(tri)
  return { parOnduleur, sans }
}

export interface EtapeAffichee {
  cle: string
  at_min: number
  quand: string
  onduleur: string | null
  /** L'étape est la fin de l'autonomie de cet onduleur (et pas un arrêt programmé avant). */
  vide: boolean
  titre: string
  tombent: string[]
  injoignables: string[]
  consequence: string
}

/**
 * Le déroulé d'une coupure, dans l'ordre des chutes. Les uid deviennent des
 * noms (ceux de `devices`), jamais affichés bruts ; un uid inconnu de la liste
 * garde son uid plutôt que de disparaître.
 */
export function deroule(doc: DocAutonomie): EtapeAffichee[] {
  const noms = new Map(doc.devices.map((d) => [d.device.uid, d.device.display_name]))
  const nom = (u: string) => noms.get(u) ?? u
  const tri = (a: string, z: string) => a.localeCompare(z, 'fr', { numeric: true })
  // L'autonomie de chaque onduleur : une étape qui tombe à cet instant est « l'onduleur se vide ».
  const autonomies = new Map(doc.ups.map((u) => [u.uid, u.runtime_min]))
  return [...doc.timeline]
    .filter((e) => Number.isFinite(e.at_min))
    .sort((a, z) => a.at_min - z.at_min)
    .map((e, i) => {
      const onduleur = e.ups_uid ? e.ups_label ?? e.ups_uid : null
      const fin = e.ups_uid ? autonomies.get(e.ups_uid) : null
      const vide = fin !== null && fin !== undefined && Math.abs(fin - e.at_min) < 1
      return {
      cle: `${i}-${e.at_min}-${e.ups_uid ?? 'secteur'}`,
      at_min: e.at_min,
      quand: e.at_min <= 0 ? 'Dès la coupure' : `+${fmtMinutes(e.at_min)}`,
      onduleur,
      vide,
      titre: !onduleur ? 'Les appareils sur secteur seul s’éteignent' : vide ? `${onduleur} se vide` : `Arrêt propre programmé (${onduleur} tient encore)`,
      tombent: e.devices.map(nom).sort(tri),
      injoignables: e.unreachable.filter((u) => !e.devices.includes(u)).map(nom).sort(tri),
      consequence: e.consequence_fr,
      }
    })
}

/* ═══════════════ Historique ═══════════════ */

/** Les plages sur batterie en millisecondes, une plage encore ouverte va jusqu'à la fin de la courbe. */
export function plagesBatterie(periodes: readonly PassageBatterie[], fin: number): [number, number][] {
  return periodes
    .map((p): [number, number] => [Date.parse(p.from), p.to ? Date.parse(p.to) : fin])
    .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b >= a)
    .sort((x, y) => x[0] - y[0])
}

/** Les courbes de l'historique, sans les séries vides. */
export function seriesTracables(doc: DocHistoriqueOnduleur): Serie[] {
  return doc.series.filter((s) => s.points.some((p: Point) => p[1] !== null))
}
